import moment from 'moment';
import { SidebarState } from 'src/components/Sidebar/hooks';
import { ENV } from 'src/constants';
import { storage } from 'src/utilities/Storage';
import numeral from 'src/utilities/numeral';
import { trackToggleShowBalance } from '../tracks';
import { ActionType, defaultAction } from '../types';

const auth = storage.get('auth');

const initialState = {
  env: storage.get('env') || ENV.PRODUCTION,
  showPasswordChangeModal: false,
  showBalance: false,
  isPasswordChangeModalDismissed: false,
  loadingPaymentInfo: true,
  merchantWebCheckout: {},
  b2xUser: {},
  ecard: {},
  banks: [],
  paymentInfo: {
    username: auth?.user?.company_username ?? '',
    email: auth?.user?.email ?? '',
    accountType: null,
    paymentCheckoutCallbackUrl: '',
    paymentCheckoutRedirectUrl: '',
    bulkDisbursement: {},
    receivingBankCode: '',
    receivingAccount: '',
    receivingAccountName: '',
    hasProdApiKey: false,
  },
  isGeneralLoading: false,
  showTrialAccountModal: storage.get('trial_account_modal') ?? false,
  showLivePhotoModal: false,
  lang: storage.get('lang') ?? 'id',
  activeHighlight: '',
  showARSettings: false,
  sidebarState: {
    width: window.innerWidth > 960 ? SidebarState.OPEN : SidebarState.COLLAPSED,
    isHover: false,
  },
  withdrawal: {
    isModalOpen: false,
    isSubmitting: false,
  },
  isTopupShow: false,
  isTopupWarningShow: false,
};

export const B2xUserAccountType = {
  APPROVED: 'APPROVED',
  NOT_SUBMITTED: 'NOT_SUBMITTED',
  SUBMITTING: 'SUBMITTING',
  SUBMITTED: 'SUBMITTED',
  NEED_REVISION: 'NEED_REVISION',
  TEMPORARILY_SUSPEND: 'TEMPORARILY_SUSPEND',
  RESUBMITTED: 'RESUBMITTED',
  REVIEWED: 'REVIEWED',
  REJECTED: 'REJECTED',
  BLOCKED: 'BLOCKED',
};

const mainReducer = (
  state = initialState,
  action: ActionType = defaultAction,
) => {
  switch (action.type) {
    case 'SET_MAIN_INFO':
      const newPendingBalance =
        action.payload.b2xUser?.pending_balance +
        action.payload.b2xUser?.hold_balance;
      return {
        ...state,
        merchantWebCheckout: action.payload.merchantWebCheckout || {},
        b2xUser:
          {
            ...action.payload.b2xUser,
            pending_balance: newPendingBalance,
          } || {},
        paymentInfo: {
          ...state.paymentInfo,
          bulkDisbursement:
            action.payload.bulkDisbursement ||
            state.paymentInfo.bulkDisbursement,
          accountType: action.payload.accountType,
          logoUrl: (action.payload.merchantWebCheckout || {}).logoUrl,
          paymentCheckoutCallbackUrl: (action.payload.merchantWebCheckout || {})
            .callbackUrl,
          paymentCheckoutRedirectUrl: (action.payload.merchantWebCheckout || {})
            .redirectUrl,
          receivingBankCode: (action.payload.merchantWebCheckout || {})
            .receivingBankCode,
          receivingAccount: (action.payload.merchantWebCheckout || {})
            .receivingAccount,
          receivingAccountName: (action.payload.merchantWebCheckout || {})
            .receivingAccountName,
          hasProdApiKey: action.payload.hasProdApiKey || false,
        },
        loadingPaymentInfo: false,
      };
    case 'UPDATE_PAYMENT_INFO':
      return {
        ...state,
        paymentInfo: {
          ...state.paymentInfo,
          ...action.payload,
        },
      };
    case 'SHOW_PASSWORD_CHANGE_MODAL':
      return {
        ...state,
        showPasswordChangeModal: true,
      };
    case 'HIDE_PASSWORD_CHANGE_MODAL':
      return {
        ...state,
        isPasswordChangeModalDismissed: true,
        showPasswordChangeModal: false,
      };
    case 'SET_ENV':
      storage.set('env', action.payload);
      return {
        ...state,
        env: action.payload,
      };
    case 'RESET_ENV': {
      const env = ENV.PRODUCTION;
      storage.set('env', env);
      return {
        ...state,
        env,
      };
    }
    case 'SET_GENERAL_LOADING':
      return {
        ...state,
        isGeneralLoading: action.payload,
      };
    case 'SET_TRIAL_ACCOUNT_MODAL':
      storage.set('trial_account_modal', action.payload);
      return {
        ...state,
        showTrialAccountModal: action.payload,
      };
    case 'SET_LIVE_PHOTO_MODAL':
      return {
        ...state,
        showLivePhotoModal: action.payload,
      };
    case 'SET_LANG':
      if (action.payload == 'en') {
        moment.locale('en');
        numeral.locale('en-idr');
      } else {
        moment.locale('id');
        numeral.locale('id-idr');
      }
      storage.set('lang', action.payload);
      return {
        ...state,
        lang: action.payload,
      };
    case 'SET_ACTIVE_HIGHLIGHT':
      return {
        ...state,
        activeHighlight: action.payload,
      };
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        sidebarState: {
          ...state.sidebarState,
          width:
            state.sidebarState.width === SidebarState.OPEN
              ? SidebarState.COLLAPSED
              : SidebarState.OPEN,
        },
      };
    case 'SET_HOVER_SIDEBAR':
      return {
        ...state,
        sidebarState: {
          ...state.sidebarState,
          isHover: action.payload,
        },
      };
    case 'SET_WITHDRAWAL':
      return {
        ...state,
        withdrawal: {
          ...state.withdrawal,
          ...action.payload,
        },
      };
    case 'SET_TOPUP_SHOW':
      return {
        ...state,
        isTopupShow: true,
      };
    case 'TOGGLE_SHOW_BALANCE':
      trackToggleShowBalance(action.payload.from, !state.showBalance);
      return {
        ...state,
        showBalance: !state.showBalance,
      };
    case 'SET_TOPUP_WARNING_SHOW':
      return {
        ...state,
        isTopupWarningShow: true,
      };
    case 'CLOSE_TOPUP_SHOW':
      return {
        ...state,
        isTopupShow: false,
      };
    case 'CLOSE_TOPUP_WARNING_SHOW':
      return {
        ...state,
        isTopupWarningShow: false,
      };
    default:
      return state;
  }
};

export default mainReducer;
